import { useAuth } from "@clerk/clerk-react";
import { convexQuery } from "@convex-dev/react-query";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useConvexAuth, useMutation } from "convex/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "#/../convex/_generated/api";
import type { Id } from "#/../convex/_generated/dataModel";
import { ListingWizard } from "#/components/sell/ListingWizard";
import { useSignInModal } from "#/hooks/useSignInModal";
import { buildListingArgs, listingToFormState } from "#/lib/listingFieldMap";
import type { ListingFormState } from "#/lib/listingFormState";
import {
	isValidDiscIdentity,
	isValidFlightCondition,
} from "#/lib/listingFormValidation";
import { toast } from "#/lib/toast";

export const Route = createFileRoute("/edit/$id")({
	component: EditListingPage,
});

function EditListingPage() {
	const { isLoading, isAuthenticated } = useConvexAuth();
	const { open } = useSignInModal();
	useEffect(() => {
		if (!isLoading && !isAuthenticated) open();
	}, [isLoading, isAuthenticated, open]);
	if (isLoading) return null;
	if (!isAuthenticated) return null;
	return <EditListingContent />;
}

function EditListingContent() {
	const { t } = useTranslation("sell");
	const { id } = Route.useParams();
	const navigate = useNavigate();
	const { userId } = useAuth();
	const updateListing = useMutation(api.listings.updateListing);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const { data: listing } = useQuery(
		convexQuery(api.listings.getById, { id: id as Id<"listings"> }),
	);

	const shouldRedirect =
		listing !== undefined &&
		(listing === null || (userId != null && listing.sellerId !== userId));

	useEffect(() => {
		if (shouldRedirect) {
			navigate({ to: "/my-listings" });
		}
	}, [shouldRedirect, navigate]);

	if (listing === undefined || shouldRedirect) return null;

	const initialState = listingToFormState(listing);

	async function handleSubmit(form: ListingFormState) {
		if (isSubmitting) return;
		if (!isValidDiscIdentity(form.discIdentity)) {
			toast.error(t("errors.discIdentityIncomplete"));
			return;
		}
		if (!isValidFlightCondition(form.flightCondition)) {
			toast.error(t("errors.flightConditionIncomplete"));
			return;
		}
		const discModelId = form.discIdentity.discModel?.id;
		setIsSubmitting(true);
		try {
			await updateListing({
				listingId: id as Id<"listings">,
				...buildListingArgs(form, discModelId as Id<"discModels">),
			});
			toast.success(t("toasts.updated"));
			navigate({ to: "/my-listings" });
		} catch (err) {
			toast.error(
				err instanceof Error ? err.message : t("errors.updateFailed"),
			);
		} finally {
			setIsSubmitting(false);
		}
	}

	return (
		<ListingWizard
			initialState={initialState}
			onSubmit={handleSubmit}
			isSubmitting={isSubmitting}
			submitLabel={t("step4.saveChanges")}
			onClose={() => navigate({ to: "/my-listings" })}
		/>
	);
}
